/* price-match.js — the Price Match / Request a price dialog
 * (snippets/price-match-modal.liquid). Loaded by sections/main-product.liquid.
 *
 * Owns: open/close with a focus trap, Esc, focus return to the opener; mode
 * switching (price_match | enquiry: title, lead, competitor-link field); the
 * honeypot; the two submit paths — native Shopify contact POST (page reload,
 * then re-open in the success state from the posted marker) or, when the
 * wrapper carries data-endpoint, a JSON POST to the app proxy with the success
 * state shown inline, with the request's reference (PM-000123) the app
 * answers. A filled honeypot is never a silent drop: [data-pm-blocked] tells
 * the person how to reach us instead (autofill can fill a hidden field). The
 * approved-price note (snippets/price-match-approved.liquid) is the one place
 * this file reads the cart: GET /cart.js, never a write.
 *
 * The two paths are two different form ELEMENTS since 2026-09-30: with an
 * endpoint the snippet renders a plain <form action="<endpoint>"> (no
 * form_type), because Shopify's hCaptcha script re-submitted the old
 * {% form 'contact' %} natively after we cancelled it — see the snippet. The
 * ?contact_posted=true arrival below only exists on the native fallback. */

/* Approved price match, "saved" state: the shopper arrived from the approval
 * email (/discount/<CODE>?redirect=/products/<h>?variant=<id>&pm=000123), so
 * Shopify has put the code on their cart, but this item is not in the cart
 * yet. Show the note only when the code on the cart is THIS request's
 * (PM000123-...), so it never appears on another product. The "applied" state
 * is server-rendered from the cart line's real discount allocation. */
(() => {
  'use strict';
  const note = document.querySelector('[data-pm-approved]');
  if (!note || !note.hidden) return;
  const pm = new URLSearchParams(location.search).get('pm');
  if (!pm || !/^\d{6}$/.test(pm)) return;
  fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((cart) => {
      const codes = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      const hit = codes.map((d) => String((d && d.code) || '').toUpperCase()).find((c) => c.indexOf('PM' + pm + '-') === 0);
      if (!hit) return;
      const text = note.querySelector('[data-pm-approved-text]');
      if (text) text.textContent = 'Price match PM-' + pm + ' approved: your code ' + hit + ' is saved to your cart. Add this item and the matched price applies at checkout.';
      note.hidden = false;
    })
    .catch(() => { /* no note is the safe failure */ });
})();

(() => {
  'use strict';
  const root = document.querySelector('[data-price-match-modal]');
  if (!root) return;

  const panel = root.querySelector('.pm__panel');
  const body = root.querySelector('[data-pm-body]');
  const success = root.querySelector('[data-pm-success]');
  const form = root.querySelector('form');
  const title = root.querySelector('[data-pm-title]');
  const lead = root.querySelector('[data-pm-lead]');
  const modeField = root.querySelector('[data-pm-mode-field]');
  const competitor = root.querySelector('[data-pm-competitor]');
  const competitorInput = competitor && competitor.querySelector('input');
  // "Their price" (the competitor's); contact[listed_price] is OURS
  const their = root.querySelector('[data-pm-their]');
  const theirInput = their && their.querySelector('input');
  const hp = root.querySelector('[data-pm-hp]');
  const fail = root.querySelector('[data-pm-fail]');
  const failText = fail ? fail.textContent : '';
  const blocked = root.querySelector('[data-pm-blocked]');
  const refBox = root.querySelector('[data-pm-ref]');
  const refValue = root.querySelector('[data-pm-ref-value]');
  const submit = root.querySelector('[data-pm-submit]');
  const endpoint = (root.getAttribute('data-endpoint') || '').trim();

  let opener = null;
  let isOpen = false;

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([type="hidden"]):not([tabindex="-1"]),select,textarea,[tabindex]:not([tabindex="-1"])';
  const focusables = () => [...panel.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);

  const setMode = (mode) => {
    const m = mode === 'enquiry' ? 'enquiry' : 'price_match';
    root.setAttribute('data-mode', m);
    if (title) title.textContent = title.getAttribute('data-title-' + m) || title.textContent;
    if (lead) lead.textContent = lead.getAttribute('data-lead-' + m) || lead.textContent;
    if (modeField) modeField.value = m;
    if (competitor) {
      competitor.hidden = m === 'enquiry';
      if (competitorInput) competitorInput.required = m !== 'enquiry';
    }
    if (their) {
      their.hidden = m === 'enquiry';
      // disabled = not submitted, so an enquiry never carries a stale price
      if (theirInput) { theirInput.required = m !== 'enquiry'; theirInput.disabled = m === 'enquiry'; }
    }
  };

  const showSuccess = (ref) => {
    if (refBox) refBox.hidden = !ref;
    if (refValue) refValue.textContent = ref || '';
    if (body) body.hidden = true;
    if (success) success.hidden = false;
    const done = success && success.querySelector('[data-pm-close]');
    if (done) done.focus();
  };

  const open = (mode, from, state) => {
    opener = from || document.activeElement;
    setMode(mode);
    if (body) body.hidden = false;
    if (success) success.hidden = true;
    if (fail) fail.hidden = true;
    if (blocked) blocked.hidden = true;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    requestAnimationFrame(() => root.classList.add('is-open'));
    isOpen = true;
    if (state === 'success') { showSuccess(); return; }
    const first = focusables().find((el) => el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') || panel;
    setTimeout(() => first.focus(), 30);
  };

  const close = () => {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    root.hidden = true;
    document.body.classList.remove('drawer-open');
    if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus();
    opener = null;
  };

  // triggers: any [data-price-match="<mode>"] on the page (buy box + call block)
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-price-match]');
    if (t) { e.preventDefault(); open(t.getAttribute('data-price-match'), t); return; }
    if (e.target.closest('[data-pm-close]') && root.contains(e.target)) { e.preventDefault(); close(); }
  });

  // keyboard: Esc closes; Tab cycles inside the panel while open
  document.addEventListener('keydown', (e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    const list = focusables();
    if (!list.length) { e.preventDefault(); panel.focus(); return; }
    const first = list[0], last = list[list.length - 1];
    if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
  });

  // the variant switcher (ui.js) changes the SKU the enquiry is about
  document.addEventListener('variant:change', (e) => {
    const v = e.detail;
    if (!v) return;
    const sku = root.querySelector('[data-pm-sku]');
    const id = root.querySelector('[data-pm-variant]');
    const listed = root.querySelector('[data-pm-listed]');
    if (sku && v.sku != null) sku.value = v.sku;
    if (id && v.id != null) id.value = v.id;
    if (listed && v.price != null) listed.value = (v.price / 100).toFixed(2);
  });

  if (form && endpoint) {
    /* App path guard (2026-09-30). The modal renders a PLAIN form here, so
     * Shopify's captcha bootstrap no longer binds it — that script called
     * HTMLFormElement.prototype.submit on the old {% form 'contact' %} after we
     * had cancelled the submit, and a second, native POST /contact went out.
     * Should any other script call form.submit() on this form, route it
     * through requestSubmit(): constraint validation runs and the submit
     * event reaches the handler below, which posts JSON and never navigates.
     * (A script calling HTMLFormElement.prototype.submit.call(form) directly
     * still bypasses this; the plain form is the fix, this is a second lock.) */
    try {
      form.submit = function () {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      };
    } catch (_) { /* a frozen form element: nothing to add */ }
  }

  if (form) {
    form.addEventListener('submit', (e) => {
      if (hp && hp.value) {
        // honeypot filled: never pretend to send (autofill can do this to a
        // real person); show the way to reach us instead
        e.preventDefault();
        if (fail) fail.hidden = true;
        if (blocked) blocked.hidden = false;
        return;
      }
      if (!endpoint) return; // native contact form: Shopify handles it, page reloads
      e.preventDefault();
      if (fail) { fail.hidden = true; fail.textContent = failText; }
      if (blocked) blocked.hidden = true;
      const fd = new FormData(form);
      const payload = {};
      fd.forEach((v, k) => { const m = k.match(/^contact\[(.+)\]$/); if (m && m[1] !== 'website') payload[m[1]] = String(v); });
      payload.page = location.href;
      submit.disabled = true;
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      }).then((r) => r.json().catch(() => ({})).then((data) => ({ r, data })))
        .then(({ r, data }) => {
          if (r.ok && data && data.ok !== false) { showSuccess(data.ref || ''); return; }
          // 422 = the app's own honeypot check: the same way out as ours
          if (r.status === 422 && blocked) { blocked.hidden = false; return; }
          // other 4xx from the app carry a sentence written for the shopper
          if (fail) {
            if (data && typeof data.message === 'string' && r.status >= 400 && r.status < 500 && r.status !== 401) fail.textContent = data.message;
            fail.hidden = false;
          }
        })
        .catch(() => { if (fail) fail.hidden = false; })
        .finally(() => { submit.disabled = false; });
    });
  }

  // arrivals: native POST round-trip (success or errors), or the quick view's
  // #request-a-price link
  const posted = root.querySelector('[data-pm-posted]');
  const errors = root.querySelector('[data-pm-errors]');
  if (posted) open(root.getAttribute('data-mode'), null, 'success');
  else if (errors) open(root.getAttribute('data-mode'), null);
  else if (location.hash === '#request-a-price' || location.hash === '#price-match') {
    open(location.hash === '#request-a-price' ? 'enquiry' : 'price_match', null);
  }
})();
